import { ethers } from 'ethers';

export interface ConversionResult {
    original: string;
    decimal: string;
    formatted: string;
    type: 'address' | 'amount' | 'timestamp' | 'hex' | 'unknown';
    confidence: number;
    suggestions: string[];
}

/**
 * Convert hex string to decimal string
 * Handles large numbers using BigNumber to avoid precision loss
 */
export function hexToDecimal(hex: string): string {
    try {
        // Remove 0x prefix if present
        const cleanHex = hex.startsWith('0x') ? hex : `0x${hex}`;

        // Validate hex string
        if (!/^0x[0-9a-fA-F]+$/.test(cleanHex)) {
            throw new Error('Invalid hex string');
        }

        // Use ethers BigNumber for large number handling
        const bigNumber = ethers.BigNumber.from(cleanHex);
        return bigNumber.toString();
    } catch (error) {
        throw new Error(`Failed to convert hex to decimal: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Convert decimal string to hex string
 */
export function decimalToHex(decimal: string): string {
    try {
        // Validate decimal string (allow integers and floats)
        if (!/^\d+(\.\d+)?$/.test(decimal)) {
            throw new Error('Invalid decimal string');
        }

        // For floating point numbers, we'll convert the integer part only
        const integerPart = decimal.split('.')[0];

        const bigNumber = ethers.BigNumber.from(integerPart);
        return bigNumber.toHexString();
    } catch (error) {
        throw new Error(`Failed to convert decimal to hex: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Format a token amount with proper decimals and symbol
 */
export function formatTokenAmount(amount: string, decimals: number = 18, symbol?: string): string {
    try {
        const bigNumber = ethers.BigNumber.from(amount);
        const formatted = ethers.utils.formatUnits(bigNumber, decimals);

        // Add comma separators
        const withCommas = addCommasSeparators(formatted);

        return symbol ? `${withCommas} ${symbol}` : withCommas;
    } catch (error) {
        return amount; // Return original if formatting fails
    }
}

/**
 * Convert hex to formatted token amount
 */
export function hexToFormattedToken(hex: string, decimals: number = 18, symbol?: string): string {
    try {
        const decimal = hexToDecimal(hex);
        return formatTokenAmount(decimal, decimals, symbol);
    } catch (error) {
        throw new Error(`Failed to convert hex to formatted token: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Add comma separators to a number string
 */
export function addCommasSeparators(number: string): string {
    try {
        // Handle decimal numbers
        const parts = number.split('.');
        const integerPart = parts[0];
        const decimalPart = parts[1];

        // Add commas to integer part
        const withCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        // Reconstruct with decimal part if it exists
        return decimalPart ? `${withCommas}.${decimalPart}` : withCommas;
    } catch (error) {
        return number; // Return original if formatting fails
    }
}

/**
 * Format large numbers with appropriate precision
 */
export function formatLargeNumber(number: string, precision: number = 4): string {
    try {
        const num = parseFloat(number);

        if (num >= 1e12) {
            return `${(num / 1e12).toFixed(precision)}T`;
        } else if (num >= 1e9) {
            return `${(num / 1e9).toFixed(precision)}B`;
        } else if (num >= 1e6) {
            return `${(num / 1e6).toFixed(precision)}M`;
        } else if (num >= 1e3) {
            return `${(num / 1e3).toFixed(precision)}K`;
        }

        return addCommasSeparators(number);
    } catch (error) {
        return number; // Return original if formatting fails
    }
}

/**
 * Detect the type of data based on patterns
 */
export function detectDataType(value: string): ConversionResult['type'] {
    // Remove whitespace
    const cleanValue = value.trim();

    // Check if it's an Ethereum address (40 hex chars with 0x prefix)
    if (/^0x[0-9a-fA-F]{40}$/.test(cleanValue)) {
        return 'address';
    }

    // Check if it's a timestamp (reasonable range for Unix timestamps)
    if (/^0x[0-9a-fA-F]+$/.test(cleanValue)) {
        try {
            const decimal = parseInt(cleanValue, 16);
            // Unix timestamp range: 1970-2100 (roughly)
            if (decimal >= 0 && decimal <= 4102444800) {
                return 'timestamp';
            }
        } catch (e) {
            // Continue to other checks
        }
    }

    // Check if it's a large hex number (likely a token amount)
    if (/^0x[0-9a-fA-F]{16,}$/.test(cleanValue)) {
        return 'amount';
    }

    // Check if it's any hex string
    if (/^0x[0-9a-fA-F]+$/.test(cleanValue)) {
        return 'hex';
    }

    return 'unknown';
}

/**
 * Smart conversion with type detection and suggestions
 */
export function smartConvert(value: string): ConversionResult {
    const original = value.trim();
    const type = detectDataType(original);
    let decimal = '';
    let formatted = '';
    let confidence = 0;
    const suggestions: string[] = [];

    try {
        if (type === 'address') {
            decimal = hexToDecimal(original);
            formatted = `Address: ${original}`;
            confidence = 0.95;
            suggestions.push('This appears to be an Ethereum address');
        } else if (type === 'timestamp') {
            decimal = hexToDecimal(original);
            const timestamp = parseInt(decimal);
            const date = new Date(timestamp * 1000);
            formatted = `${addCommasSeparators(decimal)} (${date.toLocaleString()})`;
            confidence = 0.8;
            suggestions.push('This appears to be a Unix timestamp');
        } else if (type === 'amount') {
            decimal = hexToDecimal(original);
            const withCommas = addCommasSeparators(decimal);

            // Try common token decimals
            const token18 = formatTokenAmount(decimal, 18);
            const token6 = formatTokenAmount(decimal, 6);

            formatted = withCommas;
            confidence = 0.7;
            suggestions.push(`As 18-decimal token: ${token18}`);
            suggestions.push(`As 6-decimal token: ${token6}`);
            suggestions.push('This appears to be a large number (possibly token amount)');
        } else if (type === 'hex') {
            decimal = hexToDecimal(original);
            formatted = addCommasSeparators(decimal);
            confidence = 0.6;
            suggestions.push('Basic hex to decimal conversion');
        } else {
            // Try to convert anyway
            if (original.startsWith('0x')) {
                decimal = hexToDecimal(original);
                formatted = addCommasSeparators(decimal);
                confidence = 0.3;
                suggestions.push('Converted as hex, but type uncertain');
            } else {
                decimal = original;
                formatted = original;
                confidence = 0.1;
                suggestions.push('Unable to determine type or convert');
            }
        }
    } catch (error) {
        decimal = original;
        formatted = original;
        confidence = 0;
        suggestions.push(`Conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return {
        original,
        decimal,
        formatted,
        type,
        confidence,
        suggestions
    };
}

/**
 * Validate hex string format
 */
export function isValidHex(hex: string): boolean {
    const cleanHex = hex.startsWith('0x') ? hex : `0x${hex}`;
    return /^0x[0-9a-fA-F]+$/.test(cleanHex);
}

/**
 * Validate decimal string format
 */
export function isValidDecimal(decimal: string): boolean {
    return /^\d+(\.\d+)?$/.test(decimal);
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        } else {
            // Fallback for older browsers or non-secure contexts
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const result = document.execCommand('copy');
            textArea.remove();
            return result;
        }
    } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        return false;
    }
}

/**
 * Format bytes32 hex string for better readability
 */
export function formatBytes32(hex: string): string {
    if (!hex.startsWith('0x') || hex.length !== 66) {
        return hex;
    }

    // Try to convert to string if it looks like text
    try {
        const bytes = ethers.utils.arrayify(hex);
        const text = ethers.utils.toUtf8String(bytes);
        if (text && /^[\x20-\x7E]*$/.test(text)) {
            return `"${text.replace(/\0/g, '')}"`;
        }
    } catch (e) {
        // Not valid UTF-8, return as hex
    }

    return hex;
}

// ============================================================================
// UNIT CONVERSION FUNCTIONS (Phase 2.5)
// ============================================================================

export interface NumberContext {
    type: 'eth' | 'gwei' | 'wei' | 'token' | 'gas' | 'generic';
    confidence: number;
    suggestions: string[];
    contractContext?: {
        name?: string;
        symbol?: string;
        decimals?: number;
    };
}

export interface UnitConversionResult {
    original: string;
    wei: string;
    gwei: string;
    eth: string;
    formatted: string;
    compact: string;
    token?: string;
    context: NumberContext;
}

/**
 * Detect number context based on value and function context
 */
export function detectNumberContext(
    value: string,
    functionName?: string,
    contractContext?: { name?: string; symbol?: string; decimals?: number }
): NumberContext {
    const cleanValue = value.replace(/,/g, '').trim();
    const numValue = parseFloat(cleanValue);

    let type: NumberContext['type'] = 'generic';
    let confidence = 0.3;
    const suggestions: string[] = [];

    // Function name based detection
    if (functionName) {
        const funcLower = functionName.toLowerCase();

        if (funcLower.includes('totalsupply') || funcLower.includes('balanceof')) {
            type = 'token';
            confidence = 0.8;
            suggestions.push('Detected as token amount based on function name');
        } else if (funcLower.includes('gasprice')) {
            type = 'gwei';
            confidence = 0.9;
            suggestions.push('Detected as gas price (Gwei)');
        } else if (funcLower.includes('gaslimit') || funcLower.includes('gasused')) {
            type = 'gas';
            confidence = 0.8;
            suggestions.push('Detected as gas amount');
        } else if (funcLower.includes('balance') && !contractContext?.symbol) {
            type = 'eth';
            confidence = 0.7;
            suggestions.push('Detected as ETH balance');
        }
    }

    // Value magnitude based detection
    if (type === 'generic') {
        if (numValue >= 1e18) {
            type = 'wei';
            confidence = 0.6;
            suggestions.push('Large value suggests wei amount');
        } else if (numValue >= 1e9 && numValue < 1e12) {
            type = 'gwei';
            confidence = 0.5;
            suggestions.push('Value range suggests Gwei');
        } else if (numValue > 0 && numValue < 1e6) {
            type = 'eth';
            confidence = 0.4;
            suggestions.push('Small value suggests ETH amount');
        }
    }

    // Contract context override
    if (contractContext?.symbol && type !== 'gas' && type !== 'gwei') {
        type = 'token';
        confidence = Math.max(confidence, 0.7);
        suggestions.push(`Detected as ${contractContext.symbol} token`);
    }

    return {
        type,
        confidence,
        suggestions,
        contractContext
    };
}

/**
 * Convert wei to ETH
 */
export function weiToEth(wei: string): string {
    try {
        const bigNumber = ethers.BigNumber.from(wei);
        return ethers.utils.formatEther(bigNumber);
    } catch (error) {
        return wei;
    }
}

/**
 * Convert wei to Gwei
 */
export function weiToGwei(wei: string): string {
    try {
        const bigNumber = ethers.BigNumber.from(wei);
        return ethers.utils.formatUnits(bigNumber, 9);
    } catch (error) {
        return wei;
    }
}

/**
 * Convert ETH to wei
 */
export function ethToWei(eth: string): string {
    try {
        return ethers.utils.parseEther(eth).toString();
    } catch (error) {
        return eth;
    }
}

/**
 * Convert Gwei to wei
 */
export function gweiToWei(gwei: string): string {
    try {
        return ethers.utils.parseUnits(gwei, 9).toString();
    } catch (error) {
        return gwei;
    }
}

/**
 * Smart unit conversion with context awareness
 */
export function convertWithUnits(
    value: string,
    functionName?: string,
    contractContext?: { name?: string; symbol?: string; decimals?: number }
): UnitConversionResult {
    const cleanValue = value.replace(/,/g, '').trim();
    const context = detectNumberContext(cleanValue, functionName, contractContext);

    let weiValue = cleanValue;

    // Convert input to wei based on detected type
    try {
        if (context.type === 'eth') {
            weiValue = ethToWei(cleanValue);
        } else if (context.type === 'gwei') {
            weiValue = gweiToWei(cleanValue);
        } else if (context.type === 'token' && context.contractContext?.decimals) {
            // For tokens, assume the value is in token units, convert to base units
            const bigNumber = ethers.utils.parseUnits(cleanValue, context.contractContext.decimals);
            weiValue = bigNumber.toString();
        } else {
            // Assume it's already in wei or base units
            weiValue = cleanValue;
        }
    } catch (error) {
        weiValue = cleanValue;
    }

    // Generate all format variations
    const wei = addCommasSeparators(weiValue);
    const gwei = addCommasSeparators(weiToGwei(weiValue));
    const eth = addCommasSeparators(weiToEth(weiValue));
    const formatted = addCommasSeparators(cleanValue);
    const compact = formatLargeNumber(cleanValue);

    let token: string | undefined;
    if (context.contractContext?.symbol && context.contractContext?.decimals) {
        token = formatTokenAmount(weiValue, context.contractContext.decimals, context.contractContext.symbol);
    }

    return {
        original: value,
        wei: `${wei} wei`,
        gwei: `${gwei} Gwei`,
        eth: `${eth} ETH`,
        formatted,
        compact,
        token,
        context
    };
}

/**
 * Check if a value is a decimal number (not hex)
 */
export function isDecimalNumber(value: string): boolean {
    const cleanValue = value.replace(/,/g, '').trim();
    return /^\d+(\.\d+)?$/.test(cleanValue) && !cleanValue.startsWith('0x');
}

/**
 * Extract decimal numbers from text (e.g., "Result: 1000000000.0" → "1000000000.0")
 */
export function extractDecimalFromText(text: string): string | null {
    // Look for decimal numbers in the text
    const match = text.match(/\b(\d+(?:\.\d+)?)\b/);
    return match ? match[1] : null;
}

/**
 * Check if text contains a decimal number that can be enhanced
 */
export function containsDecimalNumber(value: string): boolean {
    const extracted = extractDecimalFromText(value);
    return extracted !== null && isDecimalNumber(extracted);
}

/**
 * Enhanced smart convert that handles both hex and decimal numbers
 */
export function smartConvertEnhanced(
    value: string,
    functionName?: string,
    contractContext?: { name?: string; symbol?: string; decimals?: number }
): ConversionResult & { unitConversion?: UnitConversionResult } {
    const baseResult = smartConvert(value);

    // If it's already a decimal number, add unit conversion
    if (isDecimalNumber(value)) {
        const unitConversion = convertWithUnits(value, functionName, contractContext);
        return {
            ...baseResult,
            unitConversion
        };
    }

    // If it was converted from hex, also try unit conversion on the result
    if (baseResult.confidence > 0.3 && isDecimalNumber(baseResult.decimal)) {
        const unitConversion = convertWithUnits(baseResult.decimal, functionName, contractContext);
        return {
            ...baseResult,
            unitConversion
        };
    }

    return baseResult;
}
